import { useState } from "react";
import { api } from "@/lib/api";
import ReferencePicker from "@/components/ReferencePicker";

/** Modal that asks Claude for ONE character or reference based on the full dossier.
 *  Props: projectId, sectionKey ("personajes"|"referencias"), existingItems,
 *         onClose, onApply(item={title,meta,body})
 */
export default function SubItemAIModal({ projectId, sectionKey, existingItems, onClose, onApply }) {
  const cfg = {
    personajes: {
      label: "personaje",
      isPlural: false,
      placeholder: "Ej: una mujer mayor que vive sola junto al faro y conoce todas las leyendas del lugar…",
      quick: ["Protagonista principal.", "Personaje secundario clave.", "Un colectivo o un lugar como personaje.", "Antagonista o fuerza opuesta."],
      bodyRows: 5,
    },
    referencias: {
      label: "referencia audiovisual",
      isPlural: false,
      placeholder: "Ej: un documental sobre el agua y la memoria, registro contemplativo…",
      quick: ["Documental observacional reciente.", "Película de ficción autoral.", "Obra que dialogue con el tratamiento visual.", "Referencia sonora o de montaje."],
      bodyRows: 5,
    },
    biofilmo: {
      label: "ficha de equipo",
      isPlural: false,
      placeholder: "Ej: 'Cargo: Director de fotografía. Nombre: María Soler. Trayectoria reciente en documental observacional.'",
      quick: ["Director/a del proyecto.", "Productor/a ejecutivo/a.", "Director/a de fotografía.", "Montaje.", "Sonido directo.", "Productora (empresa)."],
      bodyRows: 6,
    },
    cartas: {
      label: "carta de intención",
      isPlural: false,
      placeholder: "Ej: 'Carta de TV3 expresando interés en coproducción para territorio catalán'.",
      quick: ["Carta de TV pública (RTVE / autonómica).", "Carta de coproducción internacional.", "Carta de distribución / venta.", "Carta de cesión de archivo.", "Carta institucional (ayuntamiento / fundación)."],
      bodyRows: 10,
    },
  }[sectionKey] || { label: "elemento", placeholder: "", quick: [], bodyRows: 5 };

  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);
  const [referenceIds, setReferenceIds] = useState([]);

  const run = async () => {
    setLoading(true); setError(""); setDraft(null);
    try {
      const r = await api.post(
        `/icaa/projects/${projectId}/ai/sub-item`,
        { section_key: sectionKey, brief, existing_items: existingItems || [], reference_ids: referenceIds },
        { timeout: 120000 }
      );
      setDraft(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 p-4" onClick={onClose} data-testid="subitem-ai-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-[#FAFAF6] shadow-2xl">
        <header className="border-b border-stone-300/60 px-6 py-4">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#26775E]">Claude · contexto completo del dossier</p>
          <h2 className="mt-0.5 font-serif text-2xl">Nuev{cfg.label.startsWith("c") || cfg.label.startsWith("f") || cfg.label.startsWith("r") ? "a" : "o"} {cfg.label}</h2>
          <p className="mt-1 text-xs italic text-stone-500">
            La IA tomará el dossier entero (sinopsis, contexto, nota de intenciones…) y propondrá una ficha coherente que no repita las {existingItems?.length || 0} ya existentes.
          </p>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <label className="text-xs uppercase tracking-widest text-stone-500">Brief opcional</label>
          <textarea value={brief} onChange={(e) => setBrief(e.target.value)} data-testid="subitem-ai-brief"
                    rows={3}
                    placeholder={cfg.placeholder}
                    className="mt-2 w-full rounded-md border border-stone-300 bg-white p-3 text-sm outline-none focus:border-[#26775E]" />

          <div className="mt-3 flex flex-wrap gap-1">
            {cfg.quick.map((q) => (
              <button key={q} onClick={() => setBrief((b) => (b ? b + " " : "") + q)}
                      className="rounded-full bg-stone-200 px-3 py-1 text-[11px] text-stone-700 hover:bg-stone-300">
                + {q}
              </button>
            ))}
          </div>

          <div className="mt-4">
            <ReferencePicker value={referenceIds} onChange={setReferenceIds} />
          </div>

          <button onClick={run} disabled={loading} data-testid="subitem-ai-run"
                  className="mt-5 w-full rounded-full bg-stone-900 px-6 py-3 text-sm uppercase tracking-widest text-[#FAFAF6] transition hover:bg-[#26775E] disabled:opacity-50">
            {loading ? "Pensando…" : `Generar ${cfg.label}`}
          </button>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

          {draft && (
            <div className="mt-6 rounded-xl border border-stone-300/70 bg-white p-5" data-testid="subitem-ai-draft">
              <p className="text-[10px] uppercase tracking-widest text-stone-500">Propuesta editable</p>
              <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                     data-testid="subitem-ai-title"
                     className="mt-2 w-full bg-transparent font-serif text-xl outline-none focus:border-b focus:border-[#26775E]" />
              <input value={draft.meta} onChange={(e) => setDraft({ ...draft, meta: e.target.value })}
                     data-testid="subitem-ai-meta"
                     className="mt-1 w-full bg-transparent text-xs uppercase tracking-widest text-[#26775E] outline-none" />
              <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                        rows={cfg.bodyRows} data-testid="subitem-ai-body"
                        className="mt-3 w-full rounded border border-stone-200 bg-[#FBFAF4] p-3 font-serif text-[13px] leading-relaxed outline-none focus:border-[#26775E] focus:bg-white" />
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-stone-300/60 bg-white px-6 py-4">
          <button onClick={onClose} data-testid="subitem-ai-cancel"
                  className="rounded-full border border-stone-400 px-5 py-2 text-xs uppercase tracking-widest text-stone-600 hover:bg-stone-100">
            Cancelar
          </button>
          <button onClick={() => draft && onApply(draft)} disabled={!draft || !draft.title}
                  data-testid="subitem-ai-apply"
                  className="rounded-full bg-[#26775E] px-6 py-2 text-xs uppercase tracking-widest text-white transition hover:bg-stone-900 disabled:opacity-40">
            Añadir al dossier
          </button>
        </footer>
      </div>
    </div>
  );
}
